import React from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAdminAccountHistoryQuery } from '../../api/admin';
import { formatDateTime } from '../../utils/dates';
import OverviewPanel from './OverviewPanel';

interface Props {
	accountId: number;
}

/** The automatic change log (crm.account_history): who changed what, newest first. */
export default function AccountHistoryPanel({ accountId }: Props) {
	const { data: entries, isLoading } = useAdminAccountHistoryQuery(accountId);

	return (
		<OverviewPanel title="History" caption="every change, logged automatically · newest first" sx={{ height: 'auto' }}>
			{isLoading ? (
				<Stack spacing={1.5}>
					<Skeleton height={32} />
					<Skeleton height={32} />
					<Skeleton height={32} />
				</Stack>
			) : !entries || entries.length === 0 ? (
				<Typography variant="body2" color="text.disabled">
					No changes logged yet.
				</Typography>
			) : (
				<Stack spacing={1.5} sx={{ maxHeight: 260, overflowY: 'auto', pr: 1 }}>
					{entries.map((e) => (
						<Box key={e.id}>
							<Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap' }}>
								<Typography variant="body2" fontWeight={600}>
									{e.summary}
								</Typography>
								<Typography variant="caption" color="text.secondary">
									{formatDateTime(e.changed_at)}
									{e.changed_by_name || e.changed_by_email ? ` · ${e.changed_by_name ?? e.changed_by_email}` : ''}
								</Typography>
							</Stack>
							{e.changes.map((c) => (
								<Typography key={c.field} variant="body2" sx={{ pl: 1.5 }}>
									<Box component="span" sx={{ color: 'text.secondary' }}>
										{c.field}:
									</Box>{' '}
									{c.old != null ? `${c.old} → ${c.new ?? ''}` : c.new ?? ''}
								</Typography>
							))}
						</Box>
					))}
				</Stack>
			)}
		</OverviewPanel>
	);
}
